import FileDropZone from "@/components/common/FileDropZone";
import { useState, useEffect } from "react";
import { guardedSave, useLeaveGuard } from "@/lib/leaveRegistry";
import { toast } from "@/lib/toast";
import { Trash2, Save, Crown, Pencil, User2, Building2, Contact, Cloud, ShieldAlert, Bell } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import FormSection from "@/components/shared/FormSection";
import Combobox from "@/components/settings/Combobox";
import EmailChangeDialog from "@/components/settings/EmailChangeDialog";
import MyTypePermissionsReadOnly from "@/components/settings/MyTypePermissionsReadOnly";
import ZohoConnectCard from "@/components/settings/ZohoConnectCard";
import { useAuth } from "@/context/AuthContext";
import { avatarColorForSeed } from "@/lib/avatarColors";
import ProfilePhoto from "@/components/ui/ProfilePhoto";
import AvatarNamePopover from "@/components/ui/AvatarNamePopover";
import api from "@/lib/api";

const ROLE_LABEL = { admin: "Administrateur", manager: "Manager", user: "Utilisateur" };
const STATUS_LABEL = { active: "Actif", invited: "En attente", blocked: "Bloqué", inactive: "Désactivé" };

const EMPTY = {
  first_name: "", last_name: "", function: "",
  default_project: "", phone: "",
};

/**
 * Mon profil — 3 sections encadrées.
 *   Identité      : Prénom, Nom
 *   Organisation  : Fonction, Rôle, Siège / Projet par défaut, Statut
 *   Contact       : E-mail (+ bouton "Modifier" pour les admins), Téléphone
 */
export default function AccountSection() {
  const { user, refresh } = useAuth();
  const isAdmin = user?.role === "admin";

  const [form, setForm] = useState(EMPTY);
  const [savingPhoto, setSavingPhoto] = useState(false);
  const [savingFields, setSavingFields] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm({
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      function: user.function || "",
      default_project: user.default_project || "",
      phone: user.phone || "",
    });
  }, [user]);

  const setField = (k) => (v) =>
    setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : (v?.target?.value ?? "") }));

  /* ------------------------------------------------------------ Photo I/O */
  const readFile = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const onPickPhoto = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Fichier invalide", { description: "Seules les images sont acceptées" });
      return;
    }
    if (file.size === 0) {
      toast.error("Fichier vide", { description: "Cette image ne contient aucune donnée" });
      return;
    }
    if (file.size > 512 * 1024) {
      toast.error("Fichier trop lourd", { description: "Max 512 Ko" });
      return;
    }
    setSavingPhoto(true);
    try {
      const dataUrl = await readFile(file);
      // Guard: base64 payload must be present and non-trivial.
      if (typeof dataUrl !== "string" || !dataUrl.includes("base64,") || dataUrl.length < 100) {
        throw new Error("empty-payload");
      }
      // Belt-and-suspenders: verify the browser can actually decode the image
      // before shipping it to the backend. Prevents "black circle" state on
      // corrupt / unsupported files that the FileReader still accepts.
      await new Promise((resolve, reject) => {
        const probe = new Image();
        probe.onload = () => (probe.naturalWidth > 0 && probe.naturalHeight > 0)
          ? resolve()
          : reject(new Error("zero-dimensions"));
        probe.onerror = () => reject(new Error("decode-failed"));
        probe.src = dataUrl;
      });
      await api.patch("/users/me", { photo: dataUrl });
      await refresh();
      toast.success("Photo mise à jour");
    } catch (err) {
      const msg = err?.response?.data?.detail
        || (err?.message === "decode-failed" || err?.message === "zero-dimensions"
            ? "L'image ne peut pas être décodée par votre navigateur."
            : err?.message === "empty-payload"
              ? "Le fichier n'a pas produit de données lisibles."
              : "Réessayez.");
      toast.error("Impossible d'enregistrer la photo", { description: msg });
    } finally { setSavingPhoto(false); }
  };

  const clearPhoto = async () => {
    setSavingPhoto(true);
    try {
      await api.patch("/users/me", { photo: null });
      await refresh();
      toast.success("Photo supprimée");
    } finally { setSavingPhoto(false); }
  };

  /* ------------------------------------------------------------ Fields I/O */
  const dirtyFields = () => {
    const dirty = {};
    for (const k of Object.keys(form)) {
      const cur = (form[k] || "").toString().trim();
      const orig = (user?.[k] || "").toString().trim();
      if (cur !== orig) dirty[k] = cur === "" ? null : cur;
    }
    return dirty;
  };
  const hasDirty = Object.keys(dirtyFields()).length > 0;
  useLeaveGuard({ dirty: hasDirty, label: "Mon profil", module: "parametres", save: guardedSave(() => saveFields()) }); // eslint-disable-line no-use-before-define

  const saveFields = async () => {
    const dirty = dirtyFields();
    if (Object.keys(dirty).length === 0) return;
    if (isAdmin && (dirty.first_name === null || dirty.last_name === null)) {
      toast.error("Nom et Prénom sont requis");
      return;
    }
    setSavingFields(true);
    try {
      await api.patch("/users/me", dirty);
      await refresh();
      toast.success("Profil mis à jour");
    } catch (err) {
      toast.error("Impossible d'enregistrer", { description: err.response?.data?.detail });
    } finally { setSavingFields(false); }
  };

  const initials =
    `${(user?.first_name?.[0] || "").toUpperCase()}${(user?.last_name?.[0] || "").toUpperCase()}`;

  return (
    <div className="space-y-6" data-testid="account-section">
      {/* --------------------------------------------------------- Photo header */}
      <Card className="bg-card border-vertec-borderDark p-6">
        <div className="flex items-center gap-6">
          <ProfilePhoto user={user} photo={user?.photo} size={96} testid="me-avatar" />
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-semibold truncate">
              {user?.first_name} {user?.last_name}
            </h2>
            <p className="text-sm text-muted-foreground truncate">
              {user?.function || <em>Fonction non renseignée</em>}
              {user?.default_project ? <> · {user.default_project}</> : null}
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              <Badge variant="outline" className="border-vertec-blue/40 text-vertec-blue">
                {ROLE_LABEL[user?.role]}
              </Badge>
              {user?.is_first_admin && (
                <Badge variant="outline" className="border-status-pending/40 text-status-pending gap-1">
                  <Crown size={12} /> Premier Admin
                </Badge>
              )}
              <Badge variant="outline" className="border-vertec-borderDark text-muted-foreground">
                {STATUS_LABEL[user?.status]}
              </Badge>
            </div>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <FileDropZone onPick={(f) => onPickPhoto(f)} multiple={false} accept="image/*" formats="JPG, PNG" disabled={savingPhoto} compact={!!user?.photo} testId="btn-upload-photo" inputTestId="me-photo-input" label={user?.photo ? "Remplacer" : "Ajouter"} />
            {user?.photo && (
              <Button
                variant="outline" size="sm"
                className="border-vertec-borderDark gap-2 text-status-danger"
                onClick={clearPhoto}
                disabled={savingPhoto}
                data-testid="btn-clear-photo"
              >
                <Trash2 size={14} /> Retirer
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* --------------------------------------------------------- Identité */}
      <SectionCard title="Identité" icon={<User2 size={16} />}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {isAdmin ? (
            <>
              <FieldInput label="Prénom *" value={form.first_name}
                onChange={setField("first_name")} testid="me-first-name" />
              <FieldInput label="Nom *" value={form.last_name}
                onChange={setField("last_name")} testid="me-last-name" />
            </>
          ) : (
            <>
              <FieldStatic label="Prénom" value={user?.first_name} />
              <FieldStatic label="Nom" value={user?.last_name} />
            </>
          )}
        </div>
      </SectionCard>

      {/* --------------------------------------------------------- Organisation */}
      <SectionCard title="Organisation" icon={<Building2 size={16} />}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {isAdmin ? (
            <>
              <div className="space-y-2">
                <Label>Fonction</Label>
                <Combobox outlineButtons
                  endpoint="/functions"
                  value={form.function}
                  onChange={setField("function")}
                  placeholder="Sélectionner une fonction…"
                  testid="me-function"
                />
              </div>
            </>
          ) : (
            <>
              <FieldStatic label="Fonction" value={user?.function} />
            </>
          )}

          <FieldStatic
            label="Rôle"
            value={
              <span className="inline-flex items-center gap-2">
                {ROLE_LABEL[user?.role]}
                {user?.is_first_admin && (
                  <Badge variant="outline" className="border-status-pending/40 text-status-pending gap-1">
                    <Crown size={12} /> Premier Admin
                  </Badge>
                )}
              </span>
            }
          />

          {isAdmin ? (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                Siège / Projet par défaut
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Optionnel</span>
              </Label>
              <Combobox outlineButtons
                endpoint="/projects"
                value={form.default_project}
                onChange={setField("default_project")}
                placeholder="Aucun siège / projet par défaut…"
                testid="me-project"
              />
              <p className="text-[11px] text-muted-foreground">
                Utilisée par le raccourci « Ajouter par siège / projet » dans les tâches.
              </p>
            </div>
          ) : (
            <FieldStatic label="Siège / Projet par défaut" value={user?.default_project} />
          )}
        </div>
      </SectionCard>

      {/* --------------------------------------------------------- Contact */}
      <SectionCard title="Contact" icon={<Contact size={16} />}>
        {(user?.zoho_email || "").trim() && <div style={{ marginBottom: 14 }}><ZohoConnectCard /></div>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">E-mail</div>
            <div className="flex items-center gap-2">
              <span className="text-sm">{user?.email}</span>
              {isAdmin && (
                <Button
                  variant="ghost" size="sm"
                  className="h-7 px-2 gap-1 text-vertec-blue hover:text-vertec-blueHover"
                  onClick={() => setEmailDialogOpen(true)}
                  data-testid="btn-change-email"
                >
                  <Pencil size={12} /> Modifier
                </Button>
              )}
            </div>
            {isAdmin && (
              <p className="text-[11px] text-muted-foreground mt-1">
                Un code sera envoyé à la nouvelle adresse avant validation.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="me-phone">Téléphone</Label>
            <Input
              id="me-phone"
              placeholder="+213 …"
              value={form.phone}
              onChange={setField("phone")}
              data-testid="me-phone-input"
            />
          </div>
        </div>
      </SectionCard>

      {/* --------------------------------------------------- Préférences */}
      <SectionCard title="Préférences" icon={<Bell size={16} />}>
        <label
          className="flex items-center justify-between gap-4 py-1 cursor-pointer select-none"
          data-testid="me-notif-sounds-row"
        >
          <div>
            <div className="text-sm font-medium flex items-center gap-2">
              🔔 Sons de notification
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              Joue un « pop » discret à la réception d&apos;un nouveau message dans une tâche suivie.
            </div>
          </div>
          <input
            type="checkbox"
            checked={user?.notification_sounds !== false}
            onChange={async (e) => {
              try {
                await api.patch("/users/me", { notification_sounds: e.target.checked });
                await refresh();
              } catch { toast.error("Impossible d'enregistrer la préférence"); }
            }}
            className="w-5 h-5 accent-[#0078D4] shrink-0"
            data-testid="me-notif-sounds-toggle"
          />
        </label>
      </SectionCard>

      {/* Droits par type — spec 2026-02-XX rev13 (point F). Rendu
          conditionnel côté composant (masqué pour les admins). */}
      <MyTypePermissionsReadOnly />

      {/* --------------------------------------------------------- Save bar */}
      <div className="flex items-center justify-end gap-2">
        <Button
          onClick={saveFields}
          disabled={!hasDirty || savingFields}
          className="bg-vertec-blue hover:bg-vertec-blueHover text-white gap-2"
          data-testid="btn-save-profile"
        >
          <Save size={14} /> {savingFields ? "Enregistrement…" : "Enregistrer les modifications"}
        </Button>
      </div>

      <EmailChangeDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        currentEmail={user?.email}
        onChanged={refresh}
      />
    </div>
  );
}

/* ---------------------------------------------------------------- Helpers */

function SectionCard({ title, icon, children }) {
  // Thin adapter kept for backwards-compat with existing testids
  // (section-identité / section-organisation / section-contact).
  return (
    <FormSection title={title} icon={icon} testIdPrefix="section" className="p-6">
      {children}
    </FormSection>
  );
}

function FieldStatic({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">{label}</div>
      <div className="text-sm">{value || <span className="text-muted-foreground">—</span>}</div>
    </div>
  );
}

function FieldInput({ label, value, onChange, testid }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={testid}>{label}</Label>
      <Input id={testid} value={value || ""} onChange={onChange} data-testid={testid} />
    </div>
  );
}

/**
 * Deterministic avatar renderer — plain <img> when a photo exists, initials
 * div otherwise. `key={photo}` forces remount so React can't leave a stale
 * <img> element around from a previous value.
 *
 * Spec 2026-02-XX rev7 (Feb 2026) — le fond du fallback initiales est
 * désormais dérivé du ``seed`` (userId, email, sinon initiales) via la
 * palette ``avatarColorForSeed``. Ainsi chaque personne conserve la même
 * couleur d'avatar à travers TOUS les modules (Tâches, Documents, TopBar,
 * KPI drill-down…). Backward-compat : sans seed → bleu Vertec.
 *
 * Spec 2026-02-XX rev8 (Feb 2026) — quand ``name`` est fourni, l'avatar
 * est en outre enveloppé dans ``<AvatarNamePopover>`` qui affiche le nom
 * complet au CLIC (indispensable sur tablette/mobile où il n'y a pas de
 * hover). Le popover s'auto-ferme après 3 s ou au clic ailleurs. Sans
 * ``name`` on garde le rendu nu (backward-compat des call-sites qui
 * n'ont pas d'info nominative).
 */
function AvatarBlock({ photo, initials, size = 40, testid, seed, name, subtitle }) {
  const box = { width: size, height: size };
  // iter399 pt 1 — initiales uniquement (la photo du profil connecté passe par ProfilePhoto).
  let inner;
  {
    const palette = seed ? avatarColorForSeed(seed) : null;
    inner = (
      <div
        className="rounded-full flex items-center justify-center font-semibold shrink-0"
        style={{
          ...box,
          fontSize: Math.max(11, Math.floor(size / 3)),
          background: palette?.bg || "#2B579A",
          color: palette?.fg || "#FFFFFF",
        }}
        data-testid={testid}
      >
        {initials || "?"}
      </div>
    );
  }
  if (name) {
    return (
      <AvatarNamePopover
        name={name}
        subtitle={subtitle}
        inline={false}
        testid={testid ? `${testid}-popover` : undefined}
      >
        {inner}
      </AvatarNamePopover>
    );
  }
  return inner;
}

export { AvatarBlock };
